import React, { useEffect, useState } from 'react'
import {
  Button,
  Flex,
  Form,
  InputNumber,
  message,
  Popconfirm,
  Table,
  Typography,
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons'
import { useUrlFilters } from 'src/libs/useUrlFilters'
import { useCoefficientsList } from './hooks'
import { updateCoefficientFx } from './model'
import { Coefficient } from './types'
import styles from './styles.module.css'

const { Text } = Typography

const FILTER_CONFIG = {
  page: { type: 'string' as const, default: '1', pagination: true },
  pageSize: { type: 'string' as const, default: '15', pagination: true },
}

type EditableDataIndex = Exclude<keyof Coefficient, 'id'>

const EDITABLE_COLUMNS: {
  title: string
  dataIndex: EditableDataIndex
  width: number
}[] = [
  { title: 'Base', dataIndex: 'base', width: 110 },
  { title: 'Deals', dataIndex: 'deals', width: 110 },
  { title: 'Real+', dataIndex: 'realAccountTradeExist', width: 110 },
  { title: 'Real-', dataIndex: 'realAccountTradeNotExist', width: 110 },
  { title: 'Lots', dataIndex: 'lots', width: 110 },
  { title: 'Buster 0.5 lots', dataIndex: 'boosterLots05', width: 130 },
  { title: 'Buster 1 lots', dataIndex: 'boosterLots1', width: 120 },
  { title: 'Profit cap', dataIndex: 'profitCap', width: 120 },
  { title: 'Max lots', dataIndex: 'maxLots', width: 120 },
]

export const CoefficientsList: React.FC = () => {
  const {
    coefficients,
    loading,
    totalCount,
    onChangeCoefficientsList,
    updateCoefficient,
    updateLoading,
  } = useCoefficientsList()

  const { filters, setFilter } = useUrlFilters(FILTER_CONFIG)

  const page = Number(filters.page) || 1
  const pageSize = Number(filters.pageSize) || 15

  const [form] = Form.useForm<Omit<Coefficient, 'id'>>()
  const [editingId, setEditingId] = useState<number | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  useEffect(() => {
    const unsub = updateCoefficientFx.doneData.watch(() => {
      message.success('Сохранено')
      setEditingId(null)
      setRefreshTrigger((prev) => prev + 1)
    })
    const unsubFail = updateCoefficientFx.failData.watch(() => {
      message.error('Ошибка. Попробуйте снова.')
    })
    return () => {
      unsub()
      unsubFail()
    }
  }, [])

  useEffect(() => {
    onChangeCoefficientsList({ limit: pageSize, page })
  }, [page, pageSize, refreshTrigger])

  const isEditing = (record: Coefficient) => record.id === editingId

  const startEdit = (record: Coefficient) => {
    const { id, ...values } = record
    form.setFieldsValue(values)
    setEditingId(id)
  }

  const cancelEdit = () => {
    setEditingId(null)
    form.resetFields()
  }

  const saveEdit = async (id: number) => {
    try {
      const values = await form.validateFields()
      updateCoefficient({ id, body: values })
    } catch {
      // validation error — AntD сам подсветит поля
    }
  }

  const columns: ColumnsType<Coefficient> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      render: (id: number) => (
        <Text strong style={{ fontSize: 14 }}>
          {id}
        </Text>
      ),
    },
    ...EDITABLE_COLUMNS.map(({ title, dataIndex, width }) => ({
      title,
      dataIndex,
      width,
      align: 'right' as const,
      render: (value: number, record: Coefficient) =>
        isEditing(record) ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[{ required: true, message: '' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        ) : (
          <Text style={{ fontSize: 14 }}>{value}</Text>
        ),
    })),
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_: any, record: Coefficient) => {
        if (isEditing(record)) {
          return (
            <Flex gap={4} justify="center">
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                title="Save"
                loading={updateLoading}
                onClick={() => saveEdit(record.id)}
              />
              <Popconfirm
                title="Cancel changes?"
                onConfirm={cancelEdit}
                okText="Yes"
                cancelText="No"
              >
                <Button size="small" icon={<CloseOutlined />} title="Cancel" />
              </Popconfirm>
            </Flex>
          )
        }
        return (
          <Flex gap={4} justify="center">
            <Button
              size="small"
              icon={<EditOutlined />}
              title="Edit"
              disabled={editingId !== null}
              onClick={() => startEdit(record)}
            />
          </Flex>
        )
      },
    },
  ]

  return (
    <div className={styles.tableWrapper}>
      <Form form={form} component={false}>
        <Table
          columns={columns}
          dataSource={coefficients}
          size="small"
          scroll={{ x: 1200 }}
          loading={loading}
          rowKey="id"
          rowClassName={(_record, index) =>
            index % 2 === 1 ? 'ant-table-row-striped' : ''
          }
          pagination={{
            current: page,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: [15, 30, 50, 100],
            total: totalCount,
            showTotal: (total, [from, to]) => `${from}\u2013${to} of ${total}`,
            size: 'default',
            position: ['topRight', 'bottomRight'],
          }}
          onChange={(pagination) => {
            if (pagination.current) {
              setFilter(
                'page',
                pagination.current === 1
                  ? undefined
                  : String(pagination.current),
              )
            }
            if (pagination.pageSize && pagination.pageSize !== pageSize) {
              setFilter('pageSize', String(pagination.pageSize))
              setFilter('page', undefined)
            }
          }}
        />
      </Form>
    </div>
  )
}
